import styled from 'styled-components';
import { useDrop, useDrag } from 'react-dnd';
import React, { useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import uniqId from '../../util/uniqId';
import getPathValue from '../../util/getPathValue';

import SettingsComponents from '.';
import Label from './Label/Label';
import Accordion, {
    AccordionDetails,
    AccordionSummary,
    AccordionDraggable,
} from '../other-components/Accordion';
import Button from '../other-components/Button';

function DraggableItems({ currentEditItem, module, handleChange: onChange }) {
    const { path, modules } = module;
    const data = getPathValue(path, currentEditItem) || [];

    const handleChange = useCallback(
        (payload, index) => {
            const { value, name } = payload;
            const copyData = [...data];
            const item = { ...copyData[index], [name]: value };
            copyData[index] = item;
            onChange({ name: path, value: copyData });
        },
        [data, onChange, path]
    );

    const moveItem = useCallback(
        (dragIndex, hoverIndex) => {
            const items = [...data];
            const [dragItem] = items.splice(dragIndex, 1);
            items.splice(hoverIndex, 0, dragItem);
            onChange({ name: path, value: items });
        },
        [data, onChange, path]
    );

    const addNewItem = useCallback(() => {
        const result = { id: uniqId() };
        modules.forEach((item) => {
            const {
                key,
                content: { defaultValue },
            } = item;
            if (key === 'label') {
                result[key] = `Item ${data.length + 1}`;
            } else {
                result[key] = defaultValue;
            }
        });
        const copyData = [...data];
        copyData.push(result);
        onChange({ name: path, value: copyData });
    }, [data, modules, onChange, path]);

    const removeItem = useCallback(
        (index) => {
            const copyData = [...data];
            copyData.splice(index, 1);
            onChange({ name: path, value: copyData });
        },
        [data, onChange, path]
    );

    const cloneItem = useCallback(
        (index) => {
            const copyData = [...data];
            const clone = { ...data[index], id: uniqId() };
            copyData.splice(index, 0, clone);
            onChange({ name: path, value: copyData });
        },
        [data, onChange, path]
    );

    return (
        <>
            <Accordion
                size="sm"
                type="box"
                onlyIconToggle
                // onExpend={handleExpend}
                expendedIcon="chevron-up"
                collapsedIcon="chevron-down"
            >
                {data.map((item, index) => (
                    <Draggable
                        item={item}
                        key={item.id}
                        index={index}
                        modules={modules}
                        moveItem={moveItem}
                        cloneItem={cloneItem}
                        removeItem={removeItem}
                        handleChange={handleChange}
                    />
                ))}
            </Accordion>

            <div style={{ marginTop: 15 }}>
                <Button
                    size="sm"
                    width="100%"
                    border="dotted"
                    onClick={addNewItem}
                >
                    + Add New
                </Button>
            </div>
        </>
    );
}

export default DraggableItems;

function RenderTemplate({ item, module, onChange, itemIndex }) {
    const value = item[module.key];
    const {
        content: { template, label },
    } = module;
    const Component = SettingsComponents[template];

    const handleChange = (payload) => onChange(payload, itemIndex);

    return (
        <>
            <br />
            {label && <Label>{label}</Label>}

            <Component
                value={value}
                name={module.key}
                onChange={handleChange}
                modContent={module.content}
            />
        </>
    );
}

const ItemTypes = {
    ACCORDION: 'ACCORDION',
};

const Text = styled.span`
    flex: 1;
    padding: 0 15px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const Icon = styled.span`
    border-style: solid;
    border-width: 0 1px 0 0;
    border-color: ${({ theme }) => theme.borderPaleGrey};
`;

function Draggable({
    item,
    index,
    modules,
    moveItem,
    cloneItem,
    removeItem,
    handleChange,
    ...restOfProps
}) {
    const itemLabel = getItemLabel(item);
    const ref = useRef(null);
    const [, drop] = useDrop({
        accept: ItemTypes.ACCORDION,
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
            // Time to actually perform the action
            moveItem(dragIndex, hoverIndex);
            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        item: { type: ItemTypes.ACCORDION, id: item.id, index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));

    return (
        <AccordionDraggable
            gap={20}
            ref={preview}
            style={{ opacity }}
            {...restOfProps}
        >
            <AccordionSummary>
                <Icon
                    ref={ref}
                    className="icon"
                    style={{
                        marginLeft: -20,
                        cursor: 'move',
                        borderRightWidth: 1,
                    }}
                >
                    <FontAwesomeIcon icon={['far', 'arrows-alt']} />
                </Icon>
                <Text>{itemLabel}</Text>
                <Icon
                    className="icon"
                    style={{ borderLeftWidth: 1 }}
                    onClick={() => cloneItem(index)}
                >
                    <FontAwesomeIcon icon={['far', 'clone']} />
                </Icon>
                <Icon className="icon" onClick={() => removeItem(index)}>
                    <FontAwesomeIcon icon={['far', 'trash-alt']} />
                </Icon>
            </AccordionSummary>
            <AccordionDetails>
                {modules.map((module, modIndx) => (
                    <RenderTemplate
                        item={item}
                        key={modIndx}
                        module={module}
                        itemIndex={index}
                        onChange={handleChange}
                    />
                ))}
            </AccordionDetails>
        </AccordionDraggable>
    );
}

function getItemLabel(item) {
    if (item.label) return item.label;
    if (item.content) {
        const el = document.createElement('div');
        el.innerHTML = item.content;
        return el.textContent;
    }
}
